import React, { useState, useEffect } from 'react';
import userService from '../services/userService';
import pipelineService from '../services/pipelineService';
import { API_URL } from '../config/api';

/**
 * AssignUsersModal Component
 * 
 * WHAT THIS DOES:
 * Shows a popup window where you can assign team members to a card
 * 
 * Think of it like assigning people to a task in Asana or Trello:
 * - Shows list of team members
 * - Click "Assign" to add someone
 * - Click "Remove" to unassign someone
 * - Has a search bar to find people quickly
 * 
 * HOW TO USE:
 * <AssignUsersModal 
 *   cardId={123} 
 *   currentAssignments={[...]} 
 *   onClose={() => setShowModal(false)} 
 *   onUpdate={() => reloadCard()} 
 * />
 */

const AssignUsersModal = ({ cardId, currentAssignments = [], onClose, onUpdate }) => {
  // STATE - these are like variables that can change
  const [availableUsers, setAvailableUsers] = useState([]); // All team members
  const [assignedUsers, setAssignedUsers] = useState(currentAssignments); // Who's assigned now
  const [loading, setLoading] = useState(true); // Are we still loading data?
  const [searchQuery, setSearchQuery] = useState(''); // What's in the search box
  const [error, setError] = useState(null); // Any error messages

  // When component loads, get the list of users
  useEffect(() => {
    loadUsers();
  }, []);

  // Get all team members from the backend (same firm only)
  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/users/firm-members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const users = await res.json();
      setAvailableUsers(users);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Assign a user to the card
  const handleAssign = async (userId) => {
    try {
      await pipelineService.assignUser(cardId, userId);
      
      // Add user to the assigned list
      const user = availableUsers.find(u => u.id === userId);
      if (user) {
        setAssignedUsers([...assignedUsers, user]);
      }
      
      // Tell parent component to refresh
      if (onUpdate) onUpdate();
      
    } catch (err) {
      console.error('Error assigning user:', err);
      setError('Failed to assign user');
    }
  };

  // Remove a user from the card
  const handleUnassign = async (userId) => {
    try {
      await pipelineService.unassignUser(cardId, userId);
      
      // Remove user from the assigned list
      setAssignedUsers(assignedUsers.filter(u => u.id !== userId));
      
      // Tell parent component to refresh
      if (onUpdate) onUpdate();
      
    } catch (err) {
      console.error('Error unassigning user:', err);
      setError('Failed to unassign user');
    }
  };

  // Filter users based on what's typed in the search box
  const filteredUsers = availableUsers.filter(user => 
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Check if a user is already assigned
  const isAssigned = (userId) => {
    return assignedUsers.some(u => u.id === userId);
  };

  // Show loading message while getting data
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-lg">
          <div className="text-center">Loading users...</div>
        </div>
      </div>
    );
  }

  // THE ACTUAL MODAL DISPLAY
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col">
        
        {/* HEADER - Title and close button */}
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-bold">Assign Team Members</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ERROR MESSAGE - Shows if something went wrong */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* CURRENTLY ASSIGNED SECTION - Shows who's already assigned */}
        {assignedUsers.length > 0 && (
          <div className="p-6 border-b bg-gray-50">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">
              Currently Assigned ({assignedUsers.length})
            </h4>
            <div className="space-y-2">
              {assignedUsers.map(user => (
                <div key={user.id} className="flex items-center justify-between bg-white p-3 rounded border">
                  <div className="flex items-center gap-3">
                    {/* User Avatar - shows profile picture or initials */}
                    {user.avatar_url ? (
                      <img
                        src={user.avatar_url}
                        alt={user.name}
                        className="w-8 h-8 rounded-full"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                        {user.name.charAt(0)}
                      </div>
                    )}
                    {/* User Info */}
                    <div>
                      <div className="font-medium text-sm">{user.name}</div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                    </div>
                  </div>
                  {/* Remove Button */}
                  <button
                    onClick={() => handleUnassign(user.id)}
                    className="text-red-500 hover:text-red-700 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SEARCH BOX */}
        <div className="p-6 border-b">
          <input
            type="text"
            placeholder="Search team members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* AVAILABLE USERS LIST - Shows team members you can assign */}
        <div className="flex-1 overflow-y-auto p-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">
            Available Team Members
          </h4>
          
          {filteredUsers.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No team members found
            </div>
          ) : (
            <div className="space-y-2">
              {filteredUsers.map(user => {
                const assigned = isAssigned(user.id);
                return (
                  <div
                    key={user.id}
                    className={`flex items-center justify-between p-3 rounded border ${
                      assigned ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* User Avatar */}
                      {user.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user.name}
                          className="w-8 h-8 rounded-full"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                          {user.name.charAt(0)}
                        </div>
                      )}
                      {/* User Info */}
                      <div>
                        <div className="font-medium text-sm">{user.name}</div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </div>
                    </div>
                    
                    {/* Assign Button or "Assigned" label */}
                    {assigned ? (
                      <span className="text-xs text-gray-500 font-medium">Assigned</span>
                    ) : (
                      <button
                        onClick={() => handleAssign(user.id)}
                        className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                      >
                        Assign
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* FOOTER - Done button */}
        <div className="p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignUsersModal;
